import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Beaker, CalendarPlus, X, Library,
  TrendingDown, Swords, Users, Fuel, CloudLightning, Leaf, ShieldAlert, Plane, Bug, Handshake,
} from "lucide-react";

const TYPE_COLORS = {
  economic: "bg-amber-100 text-amber-700",
  hr: "bg-blue-100 text-blue-700",
  competitive: "bg-rose-100 text-rose-700",
  operational: "bg-emerald-100 text-emerald-700",
  regulatory: "bg-purple-100 text-purple-700",
  technology: "bg-cyan-100 text-cyan-700",
  custom: "bg-slate-100 text-slate-700",
};

const TEMPLATE_ICONS = {
  TPL_RECESSION: TrendingDown,
  TPL_PRICE_WAR: Swords,
  TPL_PILOT_STRIKE: Users,
  TPL_FUEL_SPIKE: Fuel,
  TPL_VOLCANIC_ASH: CloudLightning,
  TPL_GREEN_MANDATE: Leaf,
  TPL_CYBER_ATTACK: ShieldAlert,
  TPL_NEW_AIRPORT: Plane,
  TPL_PANDEMIC_WAVE: Bug,
  TPL_ALLIANCE_INVITE: Handshake,
};

const DIFFICULTY_STYLE = {
  easy: "bg-emerald-100 text-emerald-700",
  medium: "bg-amber-100 text-amber-700",
  hard: "bg-rose-100 text-rose-700",
};

export function ScenarioBuilder({ games }) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);

  const activeGames = (games || []).filter(g => g.status === "active");
  const selectedGame = activeGames[0];

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scenario-templates`);
      setTemplates(res.data);
    } catch (err) { console.error("ScenarioBuilder:", err.message); }
    finally { setLoading(false); }
  }, []);

  const fetchScheduled = useCallback(async () => {
    if (!selectedGame) return;
    try {
      const res = await axios.get(`${API}/games/${selectedGame.id}/scheduled-scenarios`);
      setScheduled(res.data);
    } catch (err) { console.error("ScenarioBuilder:", err.message); }
  }, [selectedGame]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  useEffect(() => { fetchScheduled(); }, [fetchScheduled]);

  const handleSchedule = async (templateId, quarter, gameId) => {
    try {
      await axios.post(`${API}/games/${gameId}/schedule-scenario`, {
        template_id: templateId,
        quarter: parseInt(quarter),
      });
      toast.success(t("scenarioBuilder.scheduled"));
      fetchScheduled();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("scenarioBuilder.scheduleFailed"));
    }
  };

  const handleRemove = async (scheduleId, gameId) => {
    try {
      await axios.delete(`${API}/games/${gameId}/scheduled-scenarios/${scheduleId}`);
      toast.success(t("scenarioBuilder.removed"));
      fetchScheduled();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    }
  };

  // Build a map: templateId -> [{quarter, scheduleId, gameId, injected}]
  const scheduledByTemplate = {};
  scheduled.forEach(s => {
    if (!scheduledByTemplate[s.template_id]) scheduledByTemplate[s.template_id] = [];
    scheduledByTemplate[s.template_id].push({
      quarter: s.quarter,
      scheduleId: s.id,
      gameId: s.game_id,
      injected: s.injected,
    });
  });

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="h-32 bg-slate-200 rounded-lg animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scenario-builder">
      <div>
        <h3 className="text-lg font-heading font-semibold text-slate-900 flex items-center gap-2">
          <Library className="w-5 h-5 text-purple-600" />
          {t("scenarioBuilder.title")}
        </h3>
        <p className="text-sm text-slate-500">{t("scenarioBuilder.templateDesc")}</p>
      </div>

      {!selectedGame && (
        <Card>
          <CardContent className="py-8 text-center">
            <Beaker className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{t("scenarioBuilder.noActiveGame")}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map(tpl => (
          <TemplateCard
            key={tpl.id}
            template={tpl}
            game={selectedGame}
            scheduledQuarters={scheduledByTemplate[tpl.id] || []}
            onSchedule={handleSchedule}
            onRemove={handleRemove}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}


function TemplateCard({ template: tpl, game, scheduledQuarters, onSchedule, onRemove, t }) {
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const Icon = TEMPLATE_ICONS[tpl.id] || Beaker;
  const maxQ = game?.quarters_count || 8;

  const handleApply = async () => {
    if (!selectedQuarter || !game) return;
    setScheduling(true);
    await onSchedule(tpl.id, selectedQuarter, game.id);
    setSelectedQuarter("");
    setScheduling(false);
  };

  return (
    <Card className="group hover:shadow-md transition-shadow" data-testid={`template-${tpl.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="p-1.5 rounded-lg bg-slate-100 group-hover:bg-purple-100 transition-colors shrink-0">
              <Icon className="w-4 h-4 text-slate-600 group-hover:text-purple-600 transition-colors" />
            </div>
            <CardTitle className="text-base truncate">{tpl.title}</CardTitle>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${DIFFICULTY_STYLE[tpl.difficulty] || DIFFICULTY_STYLE.medium}`}>
              {tpl.difficulty}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${TYPE_COLORS[tpl.event_type] || TYPE_COLORS.custom}`}>
              {t(`scenarioBuilder.${tpl.event_type}`)}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600 line-clamp-2">{tpl.description}</p>

        <div className="flex flex-wrap gap-1">
          {(tpl.tags || []).map(tag => (
            <span key={tag} className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] text-slate-500 font-mono">{tag}</span>
          ))}
        </div>

        <div className="text-xs text-slate-500">
          {tpl.choices_count} {t("scenarioBuilder.choices").toLowerCase()}: {(tpl.choices_preview || []).join(" / ")}
        </div>

        {/* Scheduled quarters badges */}
        {scheduledQuarters.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid={`scheduled-${tpl.id}`}>
            <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">{t("scenarioBuilder.appliedIn")}:</span>
            {scheduledQuarters.map(sq => (
              <span key={sq.scheduleId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sq.injected ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                Q{sq.quarter}
                {!sq.injected && (
                  <button
                    onClick={() => onRemove(sq.scheduleId, sq.gameId)}
                    className="hover:text-rose-600 transition-colors"
                    data-testid={`remove-schedule-${sq.scheduleId}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Apply to quarter */}
        {game && (
          <div className="flex items-center gap-2 pt-1" data-testid={`apply-${tpl.id}`}>
            <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
              <SelectTrigger className="h-8 text-xs flex-1" data-testid={`quarter-select-${tpl.id}`}>
                <SelectValue placeholder={t("scenarioBuilder.selectQuarter")} />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: maxQ }, (_, i) => i + 1).map(q => (
                  <SelectItem key={q} value={String(q)}>
                    Q{q} {q <= (game.current_quarter || 0) ? `(${t("scenarioBuilder.past")})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={handleApply}
              disabled={!selectedQuarter || scheduling}
              className="gap-1 h-8 text-xs px-3"
              data-testid={`apply-btn-${tpl.id}`}
            >
              <CalendarPlus className="w-3 h-3" />
              {scheduling ? "..." : t("scenarioBuilder.apply")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
